import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import reactifyWC from 'reactify-wc';
import Heading from 'components/shared/Heading';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import Spinner from 'components/shared/Spinner';
import { validateRequiredParams } from 'helpers/url';
import useOAuthAccounts from './oAuthAccounts';
import styles from './styles.module.scss';

import { renderScopes } from './renderScopes';

const NBXSelect = reactifyWC('nbx-select');
const REQUIRED_URL_PARAMS = ['firebase_token', 'scopes', 'client_id', 'redirect_uri', 'response_type'];

export function OAuthAuthorization() {
  const { t } = useTranslation();

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  validateRequiredParams(urlParams, REQUIRED_URL_PARAMS);
  const scopes = useMemo(() => urlParams.get('scopes').split(','), [urlParams]);

  const accounts = useOAuthAccounts(urlParams.get('firebase_token'), urlParams.get('org_id'));
  const canAuthorize = useMemo(() => accounts.selected !== null, [accounts]);

  const handleSetAccount = useCallback(e => accounts.setSelected(e.detail), [accounts]);

  return (
    <div className={styles.wrapper}>
      <img src="/assets/images/logo-green.png" alt="NBX" />

      <div className={styles.confirmAuthorization}>
        <Heading tag="h2">{t('oauth_authorization_title')}</Heading>

        {accounts.loading ? (
          <div className={styles.loadingWrapper}>
            <Spinner />
          </div>
        ) : (
          <form action="https://api.nbx.com/auth/oauth2/authorize" method="POST">
            {accounts.options.length > 0 ? (
              <>
                <div className={styles.corporateAccountSelect}>
                  <NBXSelect
                    value={accounts.selected}
                    placeholder={t('oauth_account_select_placeholder')}
                    onValueChange={handleSetAccount}
                    options={accounts.options}
                  />
                </div>
                <div>
                  {t('oauth_scopes_description', {
                    scopes: renderScopes(scopes, t('common_and').toLowerCase())
                  })}{' '}
                </div>
              </>
            ) : (
              <div className={styles.accountsNotFound}>{t('oauth_accounts_not_found')}</div>
            )}

            {REQUIRED_URL_PARAMS.map(param => {
              return <input type="hidden" key={param} name={param} value={urlParams.get(param)} />;
            })}
            {urlParams.get('state') && <input type="hidden" name="state" value={urlParams.get('state')} />}
            {urlParams.get('org_id') && <input type="hidden" name="org_id" value={urlParams.get('org_id')} />}
            {accounts.selected && <input type="hidden" name="account_id" value={accounts.selected} />}

            <ButtonPrimary
              type="submit"
              noTarget
              styles={[styles.buttonAuthorize, { [styles.buttonAuthorizeDisabled]: !canAuthorize }]}
              disabled={!canAuthorize}
              fullWidth>
              {t('oauth_authorize_button')}
            </ButtonPrimary>
          </form>
        )}
      </div>
    </div>
  );
}
